import React, { JSX, forwardRef } from 'react';
import { Text as DefaultText, View as DefaultView, TextInput as DefaultTextInput, Easing, FlatList as DefaultFlatList, useWindowDimensions, Platform, TouchableWithoutFeedback, Keyboard, Pressable, PressableProps, VirtualizedList, ImageBackground, Modal as RNModal, Image, ImageSourcePropType } from 'react-native';
import { SizableText as DefaultTitle, Separator, H6, Progress, XStack } from 'tamagui';
import { Animated, StyleSheet, Dimensions } from 'react-native';
import {ScrollView as DefaultScrollView} from 'react-native-gesture-handler';
import DefaultModal, { ModalProps as DefaultModalProps } from 'react-native-modal';
import Colors from './Colors';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/types/DefaultRootState';
import { BlurViewProps, BlurView as DefaultBlurView } from '@react-native-community/blur';
import styles from '../../stylesheet';
import { useState, useMemo } from 'react';
import * as Animatable from 'react-native-animatable';
import { DropDownPickerProps as DefaultDropDownPickerProps } from 'react-native-dropdown-picker';
import DefaultDropDownPicker from 'react-native-dropdown-picker';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import AnimatedLottieView from '@utils/lottieAnimation';
import { AntDesign } from '@expo/vector-icons';
import AchievementBackground from '@assets/--native/AchievementBackground';
import { HIDE_ALL_MODALS } from '@reduxLocal/actions/types';
import Checkbox, { CheckboxProps as DefaultCheckboxProps } from 'expo-checkbox';
import { SizableText, YStack } from 'tamagui';
import { indicatorColor } from 'react-native-calendars/src/style';
import { useDeviceType } from '@hooks/useDeviceType';
import { Card as DefaultCard } from 'tamagui';
import ErrorBoundary from '@utils/ErrorBoundary';
import CloseButton from '@components/UI/CloseButton';
import { LinearGradient } from 'expo-linear-gradient';

// Conditionally import MUI LinearProgress only on web
let MuiLinearProgress: any;
if (Platform.OS === 'web') {
  try {
    MuiLinearProgress = require('@mui/material/LinearProgress').default;
  } catch (e) {
    // MUI not available, will fall back to native component
    console.warn('MUI LinearProgress not available, using native fallback');
  }
}

export function useThemeColor(
  props: { light?: string; dark?: string },
  colorName: keyof typeof Colors.light & keyof typeof Colors.dark
) {
  //TODO UNDO THIS ON DEV PREVIEW
  const isLight = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const theme = isLight ? 'light' : 'dark';
  const colorFromProps = props[theme];

  if (colorFromProps) {
    return colorFromProps;
  } else {
    return Colors[theme][colorName];
  }
}

type ThemeProps = {
  lightColor?: string;
  darkColor?: string;
  extraChildren?: React.ReactNode;
  containerStyle?: Object;
};

type CustomFlatListProps = {
  contentHeight?: number;
}

type CustomTitleProps = {
  selectable?: boolean;
  underline?: boolean;
  bold?: boolean;
};

type StyledModalInternalProps = {
  title?: string,
  caption?: string,
  hideTitleAndCaption?: boolean,
  animated?: boolean,
  hideOverflow?: boolean,
  hideCloseButton?: boolean,
  animation?: string,
  infoChildren?: JSX.Element | JSX.Element[],
  customContent?: JSX.Element | JSX.Element[];
  extraChildren?: JSX.Element | JSX.Element[];
}

type ModalOnly = {
  modalRef?: React.RefObject<StyledModalHandle>;
}

type CustomViewProps = {
  withBackground?: boolean;
}

type CustomTextProps = {
  avoidCalc?: boolean;
}

type AvatarProps = ThemeProps & {
  src?: string | ImageSourcePropType;
  alt?: string;
  size?: number;
  children?: string;
  style?: Object;
  textStyle?: Object;
}

type AvatarGroupProps = ThemeProps & {
  avatars: Array<{
    src?: string | ImageSourcePropType;
    alt?: string;
    name?: string;
  }>;
  max?: number;
  size?: number;
  spacing?: number;
  style?: Object;
  renderSurplus?: (surplus: number) => React.ReactNode;
}

export type TextProps = ThemeProps & CustomTextProps & DefaultText['props'];
export type ViewProps = ThemeProps & DefaultView['props'];
type DefaultTitleProps = React.ComponentProps<typeof DefaultTitle>;
export type TitleProps = ThemeProps & DefaultTitleProps & CustomTitleProps;
export type TextInputProps = ThemeProps & DefaultTextInput['props'];
export type ModalProps = ThemeProps & DefaultModalProps & ModalOnly & { customChildrenHeight?: number, containerStyle?: Object } & { customContent?: JSX.Element | JSX.Element[] } & { onModalWillHide?: () => void } & { modalRef?: React.RefObject<StyledModalHandle> } & { animation?: string } & { infoChildren?: JSX.Element | JSX.Element[] } & { title?: string } & { caption?: string };
export type ScrollViewProps = ThemeProps & DefaultScrollView['props'];
export type DropDownPickerProps<T> = ThemeProps & DefaultDropDownPickerProps<T>['props'];
export type FlatListProps = ThemeProps & DefaultFlatList['props'] & CustomFlatListProps;
export type StyledModalProps = Partial<ModalProps & StyledModalInternalProps & { fullScreen?: boolean }>;
export type CheckboxProps = ThemeProps & DefaultCheckboxProps;
export type CardProps = ThemeProps & React.ComponentProps<typeof DefaultCard> & { withBackground?: boolean };
export type LinearProgressProps = ThemeProps & {
  value?: number;
  variant?: 'determinate' | 'indeterminate';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'inherit';
  sx?: any;
  style?: any;
};

export const LinearProgress = (props: LinearProgressProps) => {
  const { value, variant = 'indeterminate', color = 'primary', sx, style, ...otherProps } = props;
  const theme = themeSelector();

  if (Platform.OS === 'web' && MuiLinearProgress) {
    // Use MUI LinearProgress on web
    return <MuiLinearProgress value={value} variant={variant} color={color} sx={sx} {...otherProps} />;
  }

  // Use Tamagui Progress as fallback for mobile
  return (
    <Progress
      value={value || 50}
      max={100}
      style={[
        {
          height: 4,
          borderRadius: 2,
          backgroundColor: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'
        },
        style
      ]}
      {...otherProps}
    >
      <Progress.Indicator
        animation="bouncy"
        style={{
          backgroundColor: color === 'primary' ? '#1976d2' :
            color === 'secondary' ? '#9c27b0' :
              color === 'error' ? '#d32f2f' :
                color === 'success' ? '#2e7d32' :
                  color === 'warning' ? '#ed6c02' :
                    '#1976d2'
        }}
      />
    </Progress>
  );
}

export function Card(props: CardProps & { noShadow?: boolean, cardTitle?: string, disableAnimation?: boolean, cardHeaderComponent?: React.ReactNode, glass?: boolean, elevated?: boolean, glow?: boolean, bordered?: boolean }) {
  const theme = themeSelector();
  const disableAnimation = props.disableAnimation ?? true;
  const disableShadow = props.noShadow ?? false;

  // Extract scale-related props to prevent override
  const { scale: propsScale, hoverStyle: propsHoverStyle, pressStyle: propsPressStyle, glass, elevated, glow, bordered, ...restProps } = props;

  // Ensure scale is always a number
  const scaleValue = typeof propsScale === 'number' ? propsScale : (!disableAnimation ? 0.9 : 1);
  const hoverScale = typeof propsHoverStyle?.scale === 'number' ? propsHoverStyle.scale : (!disableAnimation ? 0.925 : 1);
  const pressScale = typeof propsPressStyle?.scale === 'number' ? propsPressStyle.scale : (!disableAnimation ? 0.875 : 1);

  // Use default Card component
  return (
    <ErrorBoundary>
      <DefaultCard
        unstyled
        // animation={!disableAnimation && 'bouncy'}
        hoverStyle={{ ...propsHoverStyle, scale: hoverScale }}
        pressStyle={{ ...propsPressStyle, scale: pressScale }}
        scale={scaleValue}
        padding={props.padding || 16}
        borderRadius={props.borderRadius || 16}
        backgroundColor={props.backgroundColor || (theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)')}
        borderWidth={props.borderWidth || (bordered ? 2 : 1)}
        borderColor={props.borderColor || (theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)')}
        shadowRadius={disableShadow ? 0 : (elevated ? 20 : 10)}
        shadowOpacity={Platform.select({ web: disableShadow ? 0 : theme ? (elevated ? 0.12 : 0.08) : (elevated ? 0.25 : 0.15), default: disableShadow ? 0 : theme ? 0.6 : 0.6 })}
        shadowColor={glow ? '$primary' : (theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.4)')}
        shadowOffset={{ width: 0, height: elevated ? 12 : 4 }}
        elevation={disableShadow ? 0 : (elevated ? 8 : 2)}
        {...restProps}
      >
        {
          props.cardTitle &&
          <YStack style={{ marginBottom: 20, top: -10, margin: 10 }}>
            <XStack marginBottom={3}>
              <H6 style={{ fontWeight: 'bold', flex: 1, textAlign: 'left' }} color={theme ? 'black' : 'white'}>
                {props.cardTitle}
              </H6>
              {
                props.cardHeaderComponent
              }
            </XStack>
            <Separator />
          </YStack>
        }
        <View>
          {props.children}
        </View>
      </DefaultCard>
    </ErrorBoundary>
  )
}

// Forward static subcomponents - use default components
Card.Header = DefaultCard.Header
Card.Footer = DefaultCard.Footer
Card.Background = DefaultCard.Background
Card.Title = DefaultCard.Title
Card.Description = DefaultCard.Description
Card.Content = DefaultCard.Content

export function SmallCard(props: CardProps & { cardTitle?: string, disableAnimation?: boolean, cardHeaderComponent?: React.ReactNode }) {
  const theme = themeSelector();
  const disableAnimation = props.disableAnimation ?? true;

  // Extract scale-related props to prevent override
  const { scale: propsScale, hoverStyle: propsHoverStyle, pressStyle: propsPressStyle, ...restProps } = props;

  // Ensure scale is always a number
  const scaleValue = typeof propsScale === 'number' ? propsScale : (!disableAnimation ? 0.9 : 1);
  const hoverScale = typeof propsHoverStyle?.scale === 'number' ? propsHoverStyle.scale : (!disableAnimation ? 0.925 : 1);
  const pressScale = typeof propsPressStyle?.scale === 'number' ? propsPressStyle.scale : (!disableAnimation ? 0.875 : 1);

  // Use default Card component
  return (
    <ErrorBoundary>
      <DefaultCard
        unstyled
        animation={!disableAnimation ? 'bouncy' : undefined}
        hoverStyle={{ ...propsHoverStyle, scale: hoverScale }}
        pressStyle={{ ...propsPressStyle, scale: pressScale }}
        scale={scaleValue}
        padding={props.padding || 12}
        borderRadius={props.borderRadius || 16}
        backgroundColor={props.backgroundColor || (theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)')}
        borderWidth={props.borderWidth || 1}
        borderColor={props.borderColor || (theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)')}
        shadowRadius={8}
        shadowOpacity={theme ? 0.12 : 0.2}
        shadowColor={theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.3)'}
        shadowOffset={{ width: 0, height: 2 }}
        elevation={6}
        {...restProps}
      >
        {
          props.cardTitle &&
          <YStack style={{ marginBottom: 20, top: -10, margin: 10 }}>
            <XStack>
              <Text style={styles.title}>{props.cardTitle}</Text>
              {
                props.cardHeaderComponent
              }
            </XStack>
          </YStack>
        }
        {props.children}
      </DefaultCard>
    </ErrorBoundary>
  )
}

export function Text(props: TextProps) {
  const { style, lightColor, avoidCalc, numberOfLines, children, darkColor, ...otherProps } = props;
  const color = useThemeColor({ light: lightColor, dark: darkColor }, 'text');
  let lineNum = 1;
  const appLanguage = useSelector((state: DefaultRootState) => state.app.language);

  const calcLines = () => {
    const textLength = children?.toString().length;
    if (textLength) {
      if (textLength > 40) {
        return 3;
      } else if (textLength > 20) {
        return 2;
      }
    }
    return 1;
  }

  return Platform.OS != 'web' ?
    <DefaultText selectable={props.selectable ?? false} style={[{ flexWrap: "wrap", color, lineHeight: 30 }, style]} {...otherProps}>{children}</DefaultText>
    : <DefaultText numberOfLines={!avoidCalc && (numberOfLines ?? calcLines())} adjustsFontSizeToFit={props.adjustsFontSizeToFit ?? true} selectable={props.selectable ?? false} style={[{ flexWrap: "wrap", color, lineHeight: 30 }, style]} {...otherProps}>{children}</DefaultText>;
}

export function View(props: ViewProps & CustomViewProps) {
  const { style, lightColor, darkColor, withBackground, ...otherProps } = props;
  const backgroundColor = useThemeColor({ light: lightColor, dark: darkColor }, 'background');

  return <Animatable.View transition={'backgroundColor'} style={[withBackground && { backgroundColor }, style]} {...otherProps} />;
}


export function FlatList(props: FlatListProps) {
  const { style, data, children, contentContainerStyle, showsVerticalScrollIndicator } = props;
  const windowHeight = Dimensions.get('window').height;
  const [viewHeight, setViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const scrollY = React.useRef(new Animated.Value(0)).current;
  const theme = themeSelector();

  const indicatorHeight = viewHeight / (contentHeight / viewHeight); // Adjust scroll indicator height
  const translateY = scrollY.interpolate({
    inputRange: [0, contentHeight - viewHeight],
    outputRange: [0, viewHeight - indicatorHeight],
    extrapolate: 'clamp',
  });

  const handleScroll = typeof props.onScroll === "function" ? props.onScroll : () => { };

  return (
    <Animated.View style={{ flex: 1, flexDirection: 'row', gap: 20 }}>
      <FlatList
        {...props}
        style={[scrollStyles.scrollView, { flex: 1, backgroundColor: 'transparent', width: '100%', height: '100%' }, style]}
        onContentSizeChange={(_, eventHieght) => { console.log(eventHieght.valueOf()); setViewHeight(eventHieght.valueOf()) }}
        contentContainerStyle={[scrollStyles.contentContainer, contentContainerStyle]}
        // onScroll={Animated.event(
        //   [{ nativeEvent: { contentOffset: { y: scrollY } } }],
        //   { useNativeDriver: true }
        // )}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
      >
      </FlatList>
      {
        data?.length > 0 && viewHeight > (windowHeight) && showsVerticalScrollIndicator &&
        <View
          style={{
            backgroundColor: theme ? '#ececec' : 'grey',
            marginHorizontal: 10,
            minWidth: 10,
            paddingBottom: '20%',
            borderRadius: 999,
            overflow: 'hidden',
            marginVertical: "4%"
          }}
        >
          <Animated.View
            style={{
              width: '100%',
              position: 'absolute',
              height: indicatorHeight,
              transform: [{ translateY }],
              backgroundColor: indicatorColor || 'rgb(90,192,234)', // Adjust as needed
            }}
          />
        </View>
      }
    </Animated.View>
  )
}

export const ScrollView = React.forwardRef((
  props: ScrollViewProps & { scrollEnabled?: boolean, inverse?: boolean, indicatorColor?: string },
  ref: React.Ref<DefaultScrollView>
) => {
  const { inverse, indicatorColor, horizontal, scrollEnabled } = props;
  const [viewHeight, setViewHeight] = useState(100);
  const [viewWidth, setViewWidth] = useState(100);
  const [contentHeight, setContentHeight] = useState(200);
  const [contentWidth, setContentWidth] = useState(200);
  const scrollY = React.useRef(new Animated.Value(0)).current;
  const scrollX = React.useRef(new Animated.Value(0)).current;
  const theme = themeSelector();

  const isScrollable = horizontal
    ? contentWidth > viewWidth
    : contentHeight > viewHeight;

  const indicatorHeight = !horizontal && isScrollable
    ? Math.max(viewHeight * (viewHeight / contentHeight), 20)
    : 0;

  const indicatorWidth = horizontal && isScrollable
    ? Math.max(viewWidth * (viewWidth / contentWidth), 20)
    : 0;

  const maxVerticalScroll = Math.max(1, contentHeight - viewHeight);
  const maxHorizontalScroll = Math.max(1, contentWidth - viewWidth);

  const translateY = scrollY.interpolate({
    inputRange: [0, maxVerticalScroll],
    outputRange: [0, Math.max(0, viewHeight - indicatorHeight)],
    extrapolate: 'clamp',
  });

  const translateX = scrollX.interpolate({
    inputRange: [0, maxHorizontalScroll],
    outputRange: [0, Math.max(0, viewWidth - indicatorWidth)],
    extrapolate: 'clamp',
  });

  const handleScroll = (e) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;

    setContentHeight(contentSize.height);
    setViewHeight(layoutMeasurement.height);
    setContentWidth(contentSize.width);
    setViewWidth(layoutMeasurement.width);

    scrollY.setValue(contentOffset.y);
    scrollX.setValue(contentOffset.x);
  };

  return (
    <View style={[scrollStyles.container, {
      flexDirection: horizontal ? 'column' : (inverse ? 'row-reverse' : 'row')
    }, props.containerStyle]}>
      <DefaultScrollView
        {...props}
        ref={ref}
        style={[scrollStyles.scrollView, props.style]}
        horizontal={horizontal}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        onContentSizeChange={(w, h) => {
          setContentHeight(h);
          setContentWidth(w);
        }}
        onLayout={(e) => {
          setViewHeight(e.nativeEvent.layout.height);
          setViewWidth(e.nativeEvent.layout.width);
        }}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {props.children}
      </DefaultScrollView>
      {/* Vertical Scrollbar */}
      {!horizontal && isScrollable && scrollEnabled && (
        <View
          style={{
            backgroundColor: theme ? '#ececec' : 'grey',
            marginLeft: 20,
            minWidth: 5,
            top: 5,
            bottom: 5,
            borderRadius: 999,
            overflow: 'hidden',
            marginTop: props.style?.['marginTop'] || "10%",
            marginBottom: props.style?.['marginBottom'] || "10%",
            marginVertical: props.style?.['marginVertical'] || "10%",
          }}
        >
          <Animated.View
            style={{
              width: '100%',
              height: indicatorHeight,
              borderRadius: 999,
              transform: [{ translateY }],
              backgroundColor: indicatorColor || 'rgb(90,192,234)',
            }}
          />
        </View>
      )}

      {/* Horizontal Scrollbar */}
      {horizontal && isScrollable && scrollEnabled && (
        <View
          style={{
            backgroundColor: theme ? 'white' : 'grey',
            height: 5,
            position: 'absolute',
            bottom: 10,
            left: props.style['marginLeft'] || 5,
            right: 5,
            borderRadius: 999,
            overflow: 'hidden',
            ...props.style['marginLeft'],
            ...props.style['marginRight'],
            ...props.style['marginHorizontal'],
          }}
        >
          <Animated.View
            style={{
              height: '100%',
              width: indicatorWidth,
              borderRadius: 999,
              transform: [{ translateX }],
              backgroundColor: indicatorColor || 'rgb(90,192,234)',
            }}
          />
        </View>
      )}
    </View>
  );
});



const scrollStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: 20,
    flex: 1,
  },
  scrollIndicator: {
    position: 'absolute',
    right: 0,
    top: 0,
    width: 3, // Adjusted width of the scroll indicator
    height: '100%', // Adjusted height of the scroll indicator
    backgroundColor: 'rgb(90,192,234)', // Change to your desired color
    borderRadius: 999,
  },
});



export function Title(props: TitleProps) {
  const { style, lightColor, darkColor, bold, underline, ...otherProps } = props;
  const color = useThemeColor({ light: lightColor, dark: darkColor }, 'text');
  const theme = themeSelector();

  return <DefaultTitle selectable={props.selectable || false} style={[styles.title, { color: theme ? "black" : "white", borderBottomWidth: underline ? 1 : 0, fontWeight: bold ? 'bold' : undefined }, style]} {...otherProps} />;
}

const DismissKeyboard: React.FC<PressableProps> = ({ children, hitSlop }) => {
  const handlePress = () => Keyboard.dismiss();

  return (
    <Pressable
      onPress={handlePress}
      style={{ flex: 1 }}
      hitSlop={{ top: hitSlop, bottom: hitSlop, left: hitSlop, right: hitSlop }}
    >
      {children}
    </Pressable>
  );
};

export const TextInput: React.FC<TextInputProps> = React.memo((props: TextInputProps) => {
  const { style, lightColor = 'black', darkColor = 'white', multiline, hitSlop, ...otherProps } = props;
  const color = useThemeColor({ light: lightColor, dark: darkColor }, 'text');
  const [isFocused, setFocused] = useState(false);

  const onFocus = () => setFocused(true);
  const onBlur = () => setFocused(false);

  const textInputProps = {
    style: [styles.inputOutline, { marginHorizontal: 0, color, style }],
    cursorColor: color,
    onFocus,
    onBlur,
    ...otherProps,
  };

  return multiline ? useMemo(() => (
    <DismissKeyboard hitSlop={isFocused ? 1000 : 0}>
      <DefaultTextInput {...textInputProps} />
    </DismissKeyboard>
  ), []) : useMemo(() => (
    <DefaultTextInput {...textInputProps} />
  ), []);
});

export function CheckBox(props: CheckboxProps) {
  return (
    <Checkbox
      style={{ backgroundColor: 'transparent' }}
      color={useThemeColor({ light: 'black', dark: 'white' }, 'text')}
      {...props}
    />
  )
}

export function BlurView(props: BlurViewProps) {
  const theme = themeSelector();
  const { style, ...otherProps } = props;



  return Platform.OS === 'android' ? <View style={[{ backgroundColor: theme ? "#ececec50" : "#00000050" }, style]} {...otherProps}></View> : (<DefaultBlurView blurRadius={20} blurType={props.blurType || theme} style={[{ backgroundColor: Platform.select({ android: "transparent", default: 'transparent' }) }, style]} {...otherProps} />)
}

export function DropDownPicker<T>(props: DropDownPickerProps<T> & DropDownPickerMultipleProps<T>) {
  const theme = themeSelector();
  const { style, ...otherProps } = props;

  return <DefaultDropDownPicker
    style={[style, { backgroundColor: theme ? 'white' : '#333333' }]}
    dropDownContainerStyle={[otherProps.dropDownContainerStyle, { backgroundColor: theme ? 'white' : '#333333' }]}
    textStyle={[otherProps.textStyle, { color: theme ? 'black' : 'white' }]}
    selectedItemContainerStyle={[otherProps.selectedItemContainerStyle, { backgroundColor: theme ? 'white' : '#333333' }]}
    theme={theme ? "LIGHT" : "DARK"}
    {...otherProps}
  />;
}

export function Modal(props: ModalProps & { closeButtonStyle?: ViewProps, customChildrenHeight?: number, hideCloseButton?: boolean, setVisible: (visible: boolean) => void, backgroundColor?: string, closeFunction?: () => void }) {
  const { backgroundColor, extraChildren, customChildrenHeight, containerStyle, style, animationIn, animationOut, hasBackdrop, coverScreen, backdropColor, backdropOpacity, deviceHeight, deviceWidth, children, ...otherProps } = props;
  const theme = themeSelector();
  const { height, width } = useWindowDimensions();
  const { isTablet } = useDeviceType();
  React.useEffect(() => {
    //Add listenered for escape key to close modal
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (otherProps.isVisible) {
          otherProps.setVisible?.(false);
        } else {
          try {
            props.modalRef.current?.closeModal();
          } catch (error) {

          }
        }
      }
    }
    if (Platform.OS === 'web') {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (Platform.OS === 'web') {
        window.removeEventListener('keydown', handleKeyDown);
      }
    }
  })

  return <DefaultModal
    animationIn={'fadeIn'}
    animationOut={'zoomOutUp'}
    deviceWidth={deviceWidth || width}
    deviceHeight={deviceHeight * 1.2 || height * 1.2}
    coverScreen={coverScreen !== undefined ? coverScreen : true}
    hasBackdrop={hasBackdrop !== undefined ? hasBackdrop : true}
    backdropColor={backdropColor || 'rgba(0,0,0,0.5)'}
    backdropOpacity={backdropOpacity !== undefined ? backdropOpacity : 0.5}
    style={[styles.centerEverything, { paddingTop:  Platform.select({native: "10%", web: "5%"}), zIndex: 300, flex: 1, gap: 15 }, style]}
    {...otherProps}>
    {
      !props.hideCloseButton &&
      <CloseButton  handleClose={() => { try { otherProps.setVisible(false); props.setVisible(false); } catch { } }}
      style={props.closeButtonStyle}
      />
    }
    <View style={{
      flex: backgroundColor === "transparent" ? 0 : Platform.select({ default: 50, web: undefined }),
      backgroundColor: backgroundColor || (theme ? "white" : "#333333"),
      borderRadius: backgroundColor === "transparent" ? 0 : 20,
      minHeight: backgroundColor === "transparent" ? undefined : Platform.select({ web: "60%", default: "80%" })
    }} >
      {children}
    </View>
    {
      extraChildren &&
      <View style={{ minWidth: Platform.select({ web: "100%", default: "100%" }), top: isTablet ? "-5%" : undefined, backgroundColor: 'transparent', flex: .1, minHeight: customChildrenHeight || 70 }}>
        {extraChildren}
      </View>
    }
  </DefaultModal>;
}


export type LoadingModalHandle = {
  openModal: () => void;
  closeModal: () => void;
}

export const LoadingModal = React.forwardRef((props: ModalProps, ref) => {
  const { extraChildren, containerStyle, style, ...otherProps } = props;
  const { height, width } = useWindowDimensions();
  const [isVisible, setVisible] = useState(false);
  const theme = themeSelector();
  React.useImperativeHandle(ref, () => ({
    openModal: () => {
      setVisible(true);
    },
    closeModal: () => {
      setVisible(false);
    }
  }));

  return <DefaultModal
    animationIn={'fadeIn'}
    animationOut={'zoomOutUp'}
    deviceWidth={width}
    isVisible={isVisible}
    deviceHeight={height}
    coverScreen={true}
    hasBackdrop={true}
    style={[styles.centerEverything, { flex: 1, gap: 15, alignItems: "center" }]}
    {...otherProps}>
    <View style={[{ borderRadius: 20, maxWidth: "80%", maxHeight: "60%", backgroundColor: theme ? "white" : "black", margin: 20, marginVertical: 70, alignSelf: "center", justifyContent: "center", alignContent: "center", alignItems: "center", padding: 0, flex: 1, minWidth: "100%", overflow: "visible" }, containerStyle]}>
      <View style={styles.centerEverything}>
        <AnimatedLottieView source={require("@assets/animations/robo-anim.json")} style={{ flex: 3, minHeight: 300, width: 300 }} loop autoPlay />
        <Progress style={{ flex: 1, borderRadius: 10, maxHeight: 15, }} />
      </View>
    </View>
    {
      extraChildren &&
      <View style={{ minWidth: Platform.select({ web: undefined, default: "100%" }), maxWidth: "100%", backgroundColor: 'transparent', flex: 1, minHeight: 70 }}>
        {extraChildren}
      </View>
    }
  </DefaultModal>;
})

export type StyledModalHandle = {
  openModal: (item?: any) => void;
  closeModal: () => void;
  animateView: () => void;
  openAnimated: () => Promise<void>;
}

export const StyledModal = React.forwardRef<StyledModalHandle, StyledModalProps>((props: StyledModalProps, ref) => {
  const { title, children, animated, caption, hideTitleAndCaption, animation, infoChildren, customContent, fullScreen = false, ...otherProps } = props;
  const [visible, setVisible] = useState(false);
  const [item, setItem] = useState(null);
  const theme = themeSelector();
  const dispatch = useDispatch();
  const [animate, setAnimate] = useState(false);
  const animationRef = React.useRef<Animatable>(null);
  const { isTablet } = useDeviceType();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  React.useEffect(() => {
    //Add listenered for escape key to close modal
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (otherProps.isVisible) {
          otherProps.setVisible?.(false);
        } else {
          try {
            props.modalRef?.current?.closeModal?.();
          } catch {
            setVisible(false);
          }
        }
      }
    }
    if (Platform.OS === 'web') {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (Platform.OS === 'web') {
        window.removeEventListener('keydown', handleKeyDown);
      }
    }
  })


  React.useEffect(() => {
    //Add key listener event for escape to close modal
    if (props.animated) {
      animationRef.current?.slideInRight(200);
    }
  }, [visible])

  React.useImperativeHandle(ref, () => ({
    openModal: (item) => {
      setVisible(true);
      if (item) {
        setItem?.(item);
      }
    },
    closeModal: () => {
      setVisible(false);
    },
    animateView: () => {
      animationRef.current?.slideOutLeft(200).then(() => {
        animationRef.current?.slideInRight(200);
      })
    },
    openAnimated: async () => {
      return new Promise<void>((resolve) => {
        console.log('opening animated modal');
        setTimeout(() => {
          setAnimate(true);
          resolve();
        }, 200);
      });
    }
  }));

  if (!visible) {
    return null;
  }

  // Calculate responsive dimensions for fullScreen mode
  const getModalDimensions = () => {
    if (fullScreen) {
      return {
        width: screenWidth,
        height: screenHeight,
        maxHeight: screenHeight,
        maxWidth: screenWidth,
        margin: 0,
        padding: 0,
        borderRadius: 0
      };
    }

    return {
      width: Platform.OS === 'web' ? Math.min(600, screenWidth * 0.8) : screenWidth * 0.95,
      height: Platform.OS === 'web' ? Math.min(screenHeight * 0.8, 600) : 'auto' as const,
      maxHeight: Platform.OS === 'web' ? screenHeight * 0.65 : screenHeight * 0.85,
      maxWidth: Platform.OS === 'web' ? screenWidth * 0.6 : screenWidth * 0.95,
      margin: 20,
      padding: 20,
      borderRadius: 20
    };
  };

  const modalDimensions = getModalDimensions();

  return (
    <RNModal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={() => {
        setVisible(false);
        if (typeof ref === 'function') {
          ref(null);
        } else if (ref?.current) {
          ref.current.closeModal?.();
        }
      }}
    >
      {/* Custom backdrop */}
      <View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, ' + (fullScreen ? 0.8 : 0.5) + ')',
        zIndex: 1
      }}>
        <Pressable onPress={() => dispatch({ type: HIDE_ALL_MODALS })}>
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
        </Pressable>
      </View>
      
      {/* Modal content container */}
      <View style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2,
        margin: 0,
        padding: 0
      }}>
        <View style={{
          flexDirection: "row",
          minHeight: props.fullScreen ? "100%" : Platform.select({ web: "70vh", default: "100%" }),
          backgroundColor: theme ? "white" : "grey",
          maxHeight: props.fullScreen ? "100%" : Math.floor(screenHeight * 0.7),
          height: props.fullScreen ? "100%" : Platform.select({ web: "70vh", default: "70%" }),
          justifyContent: 'center',
          alignItems: 'center',
        borderRadius: props.fullScreen ? 0 : 20,
        minWidth: props.fullScreen ? "100vw" : (Platform.OS === 'web' ? "60%" : "100%"),
        zIndex: 0,
        width: props.fullScreen ? "100%" : undefined
      }}>
          <CloseButton style={{ position: 'absolute', top: 15, right: 10, zIndex: 10 }} handleClose={() => {
          console.log("Closing modal ");
          setVisible(false);
          if (typeof ref === 'function') {
            ref(null);
          } else if (ref?.current) {
            ref.current.closeModal?.();
          }
        }} />
        {
          (Platform.OS === 'web' || isTablet) && window.innerWidth > 800 &&
          <View style={{
            flex: 1,
            zIndex: 5,
            minWidth: 300,
            minHeight: props.fullScreen && "100%",
            maxWidth: props.fullScreen ? "100%" : 600,
            maxHeight: props.fullScreen ? "100vh" : Math.floor(screenHeight * 0.9),
            shadowRadius: props.fullScreen ? 0 : 20,
            shadowOpacity: props.fullScreen ? 0 : .5,
            borderRadius: props.fullScreen ? 0 : 20,
            left: props.fullScreen ? 0 : "-4%"
          }}>
            <View style={{
              overflow: "hidden",
              flex: 1,
              borderRadius: props.fullScreen ? 0 : 20,
              borderTopStartRadius: props.fullScreen ? 0 : 20,
              borderBottomStartRadius: props.fullScreen ? 0 : 20
            }}>
                <LinearGradient
                  colors={[
                    "rgba(6,147,227,1)",
                    "rgb(155,81,224)"
                  ]
                  }
                  style={{
                flex: 1,
                // ...StyleSheet.absoluteFillObject,
                opacity: 1,
                minWidth: props.fullScreen ? "110%" : 600,
                transform: [{ scale: 2.5 }],
                zIndex: -1,
                minHeight: props.fullScreen ? "100%" : "70vh"
              }}>
                </LinearGradient>
            </View>
            <Animatable.View transition={'minWidth'} style={{
              top: props.fullScreen ? 0 : 10,
              zIndex: 4,
              backgroundColor: '#00000066',
              marginRight: props.fullScreen ? 0 : Platform.select({ web: 30, default: 0 }),
              borderRadius: props.fullScreen ? 0 : 20,
              borderTopStartRadius: 0,
              borderBottomStartRadius: 0,
              flex: 1,
              position: "absolute",
              gap: 20,
              paddingLeft: 30,
              alignSelf: "stretch",
              minWidth: props.fullScreen && (animated ? (!animate ? "100vw" : "35vw") : "35vw"),
              overflow: props.hideOverflow ? "hidden" : undefined
            }}>
              {
                customContent ?
                  customContent :
                  <>
                    {
                      !animated &&
                      <ScrollView style={{ flex: 1, padding: props.fullScreen ? 40 : 20 }}>
                        <Title style={{ flex: 1, textAlign: "center", lineHeight: 32, fontSize: props.fullScreen ? 32 : 24, color: "white" }}>{title || "Enter title"}</Title>
                        <SizableText style={{ textAlign: 'center', flex: 1, color: "white", fontSize: props.fullScreen ? 18 : undefined }}>{caption || "Enter Caption here"}</SizableText>
                        {
                          infoChildren
                        }
                      </ScrollView>
                    }
                  </>
              }
            </Animatable.View>
          </View>
        }
        {
          ((Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth > 800) || isTablet) ?
            <Animatable.View transition={['maxWidth', 'flex', 'width']} duration={1500} style={{
              padding: props.fullScreen ? 40 : Platform.select({ web: 20, default: 10 }),
              left: props.fullScreen ? 0 : Platform.select({ web: !animate && animated ? screenWidth * -0.1 : screenWidth * -0.03, default: screenWidth * -0.03 }),
              borderTopEndRadius: props.fullScreen ? 0 : 20,
              paddingLeft: 80,
              borderBottomEndRadius: props.fullScreen ? 0 : 20,
              zIndex: -1,
              flex: !animate && animated ? 0.0001 : 1,
              alignSelf: "center",
              minHeight: props.fullScreen && "100vh",
              minWidth: props.fullScreen && "65vw",
              maxHeight: props.fullScreen ? "100%" : Math.floor(screenHeight * 0.6),
              paddingVertical: 30,
              width: !animate && animated ? 0 : (props.fullScreen ? "100%" : 400),
              maxWidth: !animate && animated ? 0 : (props.fullScreen ? "100%" : 600)
            }}>
              {
                children
              }
            </Animatable.View>
            :
            <View style={{
              padding: props.fullScreen ? 40 : 20,
              paddingVertical: props.fullScreen ? 40 : screenHeight * 0.1,
              left: props.fullScreen ? 0 : Platform.select({ web: -3, default: screenWidth * 0.002 }),
              borderTopEndRadius: props.fullScreen ? 0 : 20,
              borderBottomEndRadius: props.fullScreen ? 0 : 20,
              zIndex: -1,
              flex: 1,
              minHeight: Platform.select({ web: undefined, default: "100%" }),
              alignSelf: "center",
              minWidth: props.fullScreen ? "100%" : Platform.select({ web: undefined, default: "90%" }),
              marginHorizontal: props.fullScreen ? 0 : Platform.select({ web: undefined, default: !isTablet && -50 }),
              maxWidth: props.fullScreen ? "100%" : "90%",
              width: props.fullScreen ? "100%" : undefined
            }}>
              {!hideTitleAndCaption &&
                <YStack style={{ top: props.fullScreen ? 0 : -20, paddingHorizontal: props.fullScreen ? 20 : 10 }}>
                  <SizableText wordWrap='normal' textWrap='wrap' style={{ textAlign: "center", lineHeight: 34, fontSize: props.fullScreen ? 32 : 24 }}>{title || ""}</SizableText>
                  <SizableText textAlign='center' wordWrap='normal' textWrap='wrap' numberOfLines={6} style={{ fontSize: props.fullScreen ? 18 : undefined }}>{caption || ""}</SizableText>
                </YStack>
              }
                <View>
                {
                  children
                }
                {/*Render extra children in main container if modals fullscreen */}
              </View>
            </View>
        }
        </View>
      </View>
      
      {/* Extra children for fullScreen mode */}
      {props.fullScreen && props.extraChildren && (
        <View style={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0,
          zIndex: 3
        }}>
          {React.cloneElement(props.extraChildren)}
        </View>
      )}
      
      {/* Extra children for non-fullScreen mode */}
      {!fullScreen && props.extraChildren && (
        <View style={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          right: 20,
          zIndex: 3
        }}>
          {props.extraChildren}
        </View>
      )}
    </RNModal>
  )
});


export const Badge = ({
  badgeContent,
  color = 'red',
  children,
  max = 99,
  style,
  badgeStyle,
  ...props
}) => {
  let displayContent = badgeContent;
  if (typeof badgeContent === 'number' && badgeContent > max) {
    displayContent = `${max}+`;
  }

  return (
    <View style={[badgeStyle.container, style]} {...props}>
      {children}
      {badgeContent !== undefined && (
        <View style={[badgeStyle.badge, { backgroundColor: color }, badgeStyle]}>
          <Text style={badgeStyle.text}>{displayContent}</Text>
        </View>
      )}
    </View>
  );
};

const badgeStyle = StyleSheet.create({
  container: {
    position: 'relative',
    alignSelf: 'flex-start',
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  text: {
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
  },
});

export const Avatar = ({
  src,
  alt = '',
  size = 40,
  children,
  style,
  textStyle,
  lightColor,
  darkColor,
  ...props
}: AvatarProps) => {
  const theme = themeSelector();
  const [imageError, setImageError] = useState(false);

  // Get initials from children or alt text
  const getInitials = () => {
    const text = children || alt;
    if (!text) return '?';

    const words = text.trim().split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return text.substring(0, 2).toUpperCase();
  };

  // Generate a color based on the text
  const getBackgroundColor = () => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
      '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'
    ];
    const text = children || alt || '';
    const index = text.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const containerStyle = {
    width: size,
    height: size,
    borderRadius: size / 2,
    overflow: 'hidden' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    backgroundColor: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
  };

  const fallbackStyle = {
    ...containerStyle,
    backgroundColor: getBackgroundColor(),
  };

  const textStyles = {
    color: 'white',
    fontSize: size * 0.4,
    fontWeight: 'bold' as const,
  };

  // Handle image source
  const imageSource = typeof src === 'string'
    ? { uri: src }
    : src;

  return (
    <View style={[containerStyle, style]} {...props}>
      {imageSource && !imageError ? (
        <Image
          source={imageSource}
          style={{ width: size, height: size }}
          onError={() => setImageError(true)}
          resizeMode="cover"
        />
      ) : (
        <View style={fallbackStyle}>
          <DefaultText style={[textStyles, textStyle]}>
            {getInitials()}
          </DefaultText>
        </View>
      )}
    </View>
  );
};

export const AvatarGroup = ({
  avatars = [],
  max = 5,
  size = 40,
  spacing = -10,
  style,
  renderSurplus,
  lightColor,
  darkColor,
  ...props
}: AvatarGroupProps) => {
  const theme = themeSelector();

  // Calculate how many avatars to show
  const displayAvatars = avatars.slice(0, max);
  const surplus = Math.max(0, avatars.length - max);

  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          height: size,
        },
        style
      ]}
      {...props}
    >
      {displayAvatars.map((avatar, index) => (
        <View
          key={index}
          style={{
            marginLeft: index === 0 ? 0 : spacing,
            zIndex: displayAvatars.length - index,
            borderWidth: 2,
            borderColor: theme ? 'white' : '#333333',
            borderRadius: size / 2,
            backgroundColor: theme ? 'white' : '#333333',
          }}
        >
          <Avatar
            src={avatar.src}
            alt={avatar.alt}
            size={size}
          >
            {avatar.name || avatar.alt}
          </Avatar>
        </View>
      ))}

      {surplus > 0 && (
        <View
          style={{
            marginLeft: spacing,
            zIndex: 0,
            borderWidth: 2,
            borderColor: theme ? 'white' : '#333333',
            borderRadius: size / 2,
            backgroundColor: theme ? 'white' : '#333333',
          }}
        >
          {renderSurplus ? (
            renderSurplus(surplus)
          ) : (
            <View
              style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.2)',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <DefaultText
                style={{
                  color: theme ? 'white' : '#333333',
                  fontSize: size * 0.35,
                  fontWeight: 'bold',
                }}
              >
                +{surplus}
              </DefaultText>
            </View>
          )}
        </View>
      )}
    </View>
  );
};